import type { CatalogSnapshot, PlannedStep } from '@didto/domain';
import { importRunCommandId, isImportCommandKind } from '@didto/domain';
import { runCommand } from './api';
import type { CommandResult } from './api';
import { supabase } from './supabase';

/**
 * The menu import's reads and its one write path (ADR-044). The plan itself is made in
 * @didto/domain (planMenuImport); this file only reads the catalog as the owner sees it and sends
 * planned steps, one ordinary catalog command at a time.
 */
export const importKeys = { snapshot: ['catalog', 'import-snapshot'] as const };

/** Rows per request: the API returns at most 1000 rows to one request (PostgREST max_rows). */
const PAGE = 500;

/**
 * Every row a query gives, page by page in a stable order: a catalog larger than one page must
 * still be read whole, or rows the import already made would be planned again.
 */
async function rows<T>(
  page: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>,
): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await page(from, from + PAGE - 1);
    if (error) throw error;
    all.push(...(data ?? []));
    if (!data || data.length < PAGE) return all;
  }
}

/** The whole catalog of the owner's business (row security), archived rows and Loyverse links included. */
export async function fetchImportSnapshot(): Promise<CatalogSnapshot> {
  const [categories, products, variants, costs, groups, options, assignments, routes] =
    await Promise.all([
      rows<CatalogSnapshot['categories'][number]>((a, b) =>
        supabase
          .from('category')
          .select('id, name, archived_at, external_refs')
          .order('id')
          .range(a, b),
      ),
      rows<CatalogSnapshot['products'][number]>((a, b) =>
        supabase
          .from('product')
          .select(
            'id, name, category_id, description, production_route_id, archived_at, external_refs',
          )
          .order('id')
          .range(a, b),
      ),
      rows<CatalogSnapshot['variants'][number]>((a, b) =>
        supabase
          .from('product_variant')
          .select('id, product_id, name, sku, barcode, price_minor, is_default, archived_at')
          .order('id')
          .range(a, b),
      ),
      rows<CatalogSnapshot['costs'][number]>((a, b) =>
        supabase
          .from('product_variant_cost')
          .select('product_variant_id, cost_minor')
          .order('product_variant_id')
          .range(a, b),
      ),
      rows<CatalogSnapshot['groups'][number]>((a, b) =>
        supabase
          .from('modifier_group')
          .select('id, name, min_select, max_select, archived_at, external_refs')
          .order('id')
          .range(a, b),
      ),
      rows<CatalogSnapshot['options'][number]>((a, b) =>
        supabase
          .from('modifier_option')
          .select('id, modifier_group_id, name, price_delta_minor, archived_at')
          .order('id')
          .range(a, b),
      ),
      rows<CatalogSnapshot['assignments'][number]>((a, b) =>
        supabase
          .from('product_modifier_group')
          .select('product_id, modifier_group_id')
          .order('product_id')
          .order('modifier_group_id')
          .range(a, b),
      ),
      rows<CatalogSnapshot['routes'][number]>((a, b) =>
        supabase
          .from('production_route')
          .select('id, code')
          .is('archived_at', null)
          .order('id')
          .range(a, b),
      ),
    ]);
  return { categories, products, variants, costs, groups, options, assignments, routes };
}

/**
 * Send one planned step in an import run. Its command id comes from the run and the step: sent
 * again in the same run (a retry after a dropped connection) it is recognised by the server and not
 * applied twice; a later run sends it afresh. Anything that is not a catalog command is refused
 * here, before it leaves the browser.
 */
export async function runImportStep(step: PlannedStep, runId: string): Promise<CommandResult> {
  if (!isImportCommandKind(step.kind))
    throw new Error(`invalid_payload: The import only sends catalog commands (got ${step.kind}).`);
  return runCommand(step.kind, step.payload as never, {
    commandId: importRunCommandId(runId, step),
  });
}
